import { List, styled } from '@mui/material';

import { useEffect, useLayoutEffect, useRef } from 'react';

import { Skeleton } from '@app-velocity/ui';

import { SingleAiResponse } from './components/SingleAiResponse';
import { Loop } from './components/Loop';
import { useAiChat } from './hooks/useAiChat';
import { useCycleDetection } from './hooks/useCycleDetection';
import { LoopContext } from './context/LoopContext';
import { NextAiResponseLoading } from './components/NextAiResponseLoading';
import { useAiChatContext } from './hooks/useAiChatContext';

const ResponsesList = styled(List)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

export const AIChat = () => {
  const scrolledElementRef = useRef<HTMLDivElement>(null);
  const { messages, refetch: refetchMessages, isLoading } = useAiChat();
  const { review, persona } =
    useAiChatContext();
  const detectedCycle = useCycleDetection(messages);

  useEffect(() => {
    if (review.reviewStatus === 'IN_PROGRESS') return;

    refetchMessages();
  }, [review]);

  useLayoutEffect(() => {
    if (review.reviewStatus !== 'IN_PROGRESS') return;

    setTimeout(() => {
      scrolledElementRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, [messages, scrolledElementRef, review]);

  const cycledElementsLength = detectedCycle
    ? detectedCycle.occurences * detectedCycle.cycle.length
    : 0;
  const unaffectedSteps = detectedCycle
    ? messages.length - cycledElementsLength
    : messages.length;

  const unaffectedMessages = messages.slice(0, unaffectedSteps);
  const affectedMessages = messages.slice(-cycledElementsLength);

  return (
    <>
      {persona === review.persona && messages.length > 0 ? (
        <div>
          <ResponsesList disablePadding dense>
            {unaffectedMessages.map((message, index) => (
              <SingleAiResponse
                key={index}
                message={message}
                stepNumber={index + 1}
              />
            ))}
            {detectedCycle ? (
              <LoopContext.Provider
                value={{
                  cycleLength: detectedCycle.cycle.length,
                  unaffectedMessagesCount: unaffectedMessages.length,
                  messages: affectedMessages,
                }}
              >
                <Loop />
              </LoopContext.Provider>
            ) : null}
          </ResponsesList>
          <div ref={scrolledElementRef} />
        </div>
      ) : null}
      {review.reviewStatus === 'IN_PROGRESS' ? (
        <NextAiResponseLoading stepNumber={unaffectedMessages.length + 1} />
      ) : null}
      {review.persona !== persona ||
      ((!review || review.reviewStatus !== 'IN_PROGRESS') && isLoading) ? (
        <Skeleton height={159} margin="0" variant="light" />
      ) : null}
    </>
  );
};
